/**
 * A régua do controle «Tipo de viewer» do gerenciador (29/09/2026).
 *
 * Três estados por arquivo: automático (a triagem decide, o padrão), documento e
 * embalagem. Mora fora do `.tsx` porque o Node não testa JSX: quem pode trocar,
 * o que a tela diz antes e depois, e o que a resposta muda no card.
 *
 * Trocar de lado prepara o arquivo de novo e refaz o render — são as duas
 * metades que o servidor faz juntas (`services/viewer_kind_override.py`). Até
 * a preparação terminar o arquivo continua abrindo como estava; por isso o
 * card mostra a escolha («manual») antes de mostrar o viewer novo. Se a
 * preparação da rota nova falhar, o servidor desfaz a troca: o arquivo segue
 * abrindo como antes, a escolha volta à anterior e o Histórico registra
 * «Troca de viewer desfeita» com o motivo.
 */
import type { AssetListItem, AssetStatus, ViewerKind } from "./api";

export type ViewerKindChoice = "auto" | ViewerKind;

export const VIEWER_KIND_CHOICES: readonly ViewerKindChoice[] = ["auto", "document", "packaging"];

/** O que o servidor fez com o pedido — ver `ViewerKindChangeOut.acao`. */
export type ViewerKindAction = "sem_troca" | "gravado" | "preparando" | "render";

export interface ViewerKindChange {
  file_id: string;
  asset_id: string;
  /** A rota que o arquivo abre agora; em `preparando` ainda é a antiga. */
  viewer_kind: ViewerKind;
  viewer_kind_override: ViewerKind | null;
  acao: ViewerKindAction;
  job_id: string | null;
}

type AssetDoControle = Pick<
  AssetListItem,
  "viewer_kind" | "viewer_kind_override" | "source_type" | "current_file_id" | "status" | "separation_count"
>;

/** Os mesmos estados que desabilitam o «Renderizar» na lista e no card. */
const EM_VOO: ReadonlySet<AssetStatus> = new Set(["queued", "rendering", "preparing"]);

function rotaAtual(asset: AssetDoControle): ViewerKind {
  return asset.viewer_kind === "document" ? "document" : "packaging";
}

/** A escolha em vigor: a feita à mão, ou automático. */
export function escolhaAtual(asset: AssetDoControle): ViewerKindChoice {
  const override = asset.viewer_kind_override;
  return override === "document" || override === "packaging" ? override : "auto";
}

/**
 * Só PDF (e `.ai`, que entra como PDF) tem as duas rotas: imagem e conjunto de
 * separações abrem sempre no viewer de embalagem, e o servidor recusa com 409.
 */
export function podeTrocarViewer(asset: AssetDoControle): boolean {
  return (
    Boolean(asset.current_file_id) &&
    String(asset.source_type ?? "").toLowerCase() === "pdf" &&
    !asset.separation_count
  );
}

/**
 * O «Renderizar com camadas» aparece onde há camadas OCG para ler: no PDF de
 * embalagem. Documento vai pelo PDFium, que não tem camadas no viewer; conjunto
 * de separações e raster não têm OCG nenhum.
 */
export function podeRenderizarComCamadas(asset: AssetDoControle): boolean {
  return podeTrocarViewer(asset) && rotaAtual(asset) === "packaging";
}

/** Por que a troca está bloqueada agora, ou `null`. */
export function bloqueioDaTroca(asset: AssetDoControle): string | null {
  if (EM_VOO.has(asset.status)) {
    return "O arquivo está sendo preparado ou renderizado. Espere terminar para trocar o viewer.";
  }
  return null;
}

export function rotuloDaEscolha(escolha: ViewerKindChoice): string {
  if (escolha === "document") return "Documento";
  if (escolha === "packaging") return "Embalagem";
  return "Automático";
}

export function descricaoDaEscolha(escolha: ViewerKindChoice): string {
  if (escolha === "document") return "Texto corrido, busca e páginas — manuais, bulas, cronogramas.";
  if (escolha === "packaging") return "Separações de cor e inspeção em alta resolução — artes de embalagem.";
  return "A triagem decide pelo conteúdo do PDF.";
}

/** «Abre como Documento · automático» — o estado numa linha. */
export function resumoDoEstado(asset: AssetDoControle): string {
  const rota = rotuloDaEscolha(rotaAtual(asset));
  const escolha = escolhaAtual(asset);
  if (escolha === "auto") return `Abre como ${rota} · automático`;
  if (escolha !== rotaAtual(asset)) {
    // A escolha já foi gravada e a preparação que a aplica ainda não terminou.
    return `Abre como ${rota} · escolhido ${rotuloDaEscolha(escolha)}, preparando`;
  }
  return `Abre como ${rota} · escolhido à mão`;
}

/**
 * O texto de confirmação, ou `null` quando não há o que confirmar (a escolha é
 * a que já vale). A frase avisa o custo — preparar e renderizar de novo — e que
 * o arquivo continua abrindo como está enquanto isso.
 */
export function confirmacaoDaTroca(asset: AssetDoControle, escolha: ViewerKindChoice): string | null {
  if (escolha === escolhaAtual(asset)) return null;
  if (escolha === "auto") {
    return (
      "A triagem automática volta a decidir. Se ela escolher o outro viewer, " +
      "o arquivo será preparado e renderizado de novo."
    );
  }
  const alvo = rotuloDaEscolha(escolha).toLowerCase();
  if (escolha === rotaAtual(asset)) {
    return `O arquivo já abre como ${alvo}: a escolha fica gravada e nada é refeito.`;
  }
  return (
    `O arquivo será preparado e renderizado de novo para abrir como ${alvo}. ` +
    "Enquanto isso, ele continua abrindo como está; se a troca não der certo, " +
    "ele volta ao que era e o Histórico diz por quê."
  );
}

/** O que a resposta muda no card, sem esperar a próxima listagem. */
export function patchDaTroca(resposta: ViewerKindChange): Partial<AssetListItem> {
  const patch: Partial<AssetListItem> = {
    viewer_kind: resposta.viewer_kind,
    viewer_kind_override: resposta.viewer_kind_override,
  };
  if (resposta.acao === "preparando") patch.status = "preparing";
  if (resposta.acao === "render") patch.status = "queued";
  return patch;
}

export function mensagemDoResultado(resposta: ViewerKindChange): string {
  const rota = rotuloDaEscolha(resposta.viewer_kind).toLowerCase();
  switch (resposta.acao) {
    case "sem_troca":
      return `Nada a refazer: o arquivo já abre como ${rota}.`;
    case "gravado":
      return "Escolha gravada: vale na próxima preparação do arquivo (use «Renderizar» em um minuto).";
    case "preparando":
      return "Preparando o arquivo de novo: o viewer é atualizado quando terminar.";
    case "render":
      return `Renderizando de novo como ${rota}.`;
  }
}

/** Os eventos da trilha (`asset_events`) que a troca de viewer grava. */
export const EVENTOS_DA_TROCA: ReadonlySet<string> = new Set([
  "viewer_kind_changed",
  "viewer_kind_change_failed",
]);

/**
 * «Automático → Documento» para o Histórico do arquivo, a partir dos eventos
 * `viewer_kind_changed` e `viewer_kind_change_failed` (`de`/`para` no
 * metadata; no segundo, `para` é a escolha à qual a troca desfeita voltou).
 * `null` quando o evento não traz os dois — o Histórico mostra só o rótulo.
 */
export function detalheDaTrocaNoHistorico(
  eventType: string,
  metadata: Record<string, unknown> | null,
): string | null {
  if (!EVENTOS_DA_TROCA.has(eventType)) return null;
  if (!metadata) return null;
  const valido = (valor: unknown): valor is ViewerKindChoice =>
    typeof valor === "string" && (VIEWER_KIND_CHOICES as readonly string[]).includes(valor);
  const de = metadata["de"];
  const para = metadata["para"];
  if (!valido(de) || !valido(para)) return null;
  return `${rotuloDaEscolha(de)} → ${rotuloDaEscolha(para)}`;
}
